
import React, { useEffect, useRef } from 'react';
import CameraSetup from './VideoRecorder/CameraSetup';
import { useVideoRecorder } from './VideoRecorder/useVideoRecorder';
import RecordingControls from './VideoRecorder/RecordingControls';
import RecordingTimer from './VideoRecorder/RecordingTimer';
import PipDisplay from './VideoRecorder/PipDisplay';
import RecordedVideoReview from './VideoRecorder/RecordedVideoReview';
import EmotionDetector from './EmotionDetector';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { useParams } from 'react-router-dom';

interface DualCameraViewProps {
  isRecordingScreen?: boolean;
  mainVideoSrc?: string;
  merchantId?: string;
}

const DualCameraView: React.FC<DualCameraViewProps> = ({
  isRecordingScreen = false,
  mainVideoSrc = '',
  merchantId: propMerchantId
}) => {
  const { id } = useParams<{ id: string }>();
  
  const {
    mainVideoRef,
    pipVideoRef,
    canvasRef,
    isRecording,
    recordingTime,
    recordedVideo,
    currentEmotion,
    currentEmoji,
    currentEmotionPercentage,
    dominantEmotion,
    dominantEmoji,
    dominantEmotionPercentage,
    selectedEmotion,
    selectedEmoji,
    isReadyToRecord,
    isSaving,
    title,
    setTitle,
    description,
    setDescription,
    merchantId,
    setMerchantId,
    handleStreamsReady,
    handleReadyToRecord,
    startRecording,
    stopRecording,
    handleEmotionDetected,
    handleEmotionFrequencyUpdate,
    selectAsDominantEmotion,
    shareToSocialMedia,
    shareToAllSocialMedia,
    saveRecordingAndSignIn,
    cancelRecording
  } = useVideoRecorder();
  
  // Set merchant ID from props or URL parameters
  useEffect(() => {
    if (propMerchantId) {
      console.log('DualCameraView - Setting merchant ID from props:', propMerchantId);
      setMerchantId(propMerchantId);
    } else if (id) {
      console.log('DualCameraView - Setting merchant ID from URL params:', id);
      setMerchantId(id);
    }
  }, [propMerchantId, id, setMerchantId]);

  // Debug merchantId to ensure it's being properly passed
  useEffect(() => {
    console.log('Current merchantId in DualCameraView:', merchantId);
  }, [merchantId]);

  return (
    <div className="relative w-full h-full min-h-screen bg-black overflow-hidden">
      {/* Main video display with vertical aspect ratio for mobile */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-full max-w-md h-full">
          {/* Use 9:16 aspect ratio (vertical video) with improved quality */}
          <AspectRatio ratio={9/16} className="mx-auto h-full">
            <video 
              ref={mainVideoRef}
              className="w-full h-full object-cover"
              autoPlay
              playsInline
              muted
            />
          </AspectRatio>
        </div>
      </div>

      {/* PIP Display for front camera with enhanced emotion display */}
      <PipDisplay 
        videoRef={pipVideoRef}
        currentEmotion={currentEmotion || 'Detecting...'}
        currentEmoji={currentEmoji || '/emojis/Neutral.png'}
        emotionPercentage={currentEmotionPercentage || '50%'}
        isRecording={isRecording}
      />

      {/* Canvas for recording (hidden) with 9:16 vertical video resolution */}
      <canvas 
        ref={canvasRef}
        className="hidden"
        width="1080"  
        height="1920"  
      />

      {/* Camera setup component */}
      <CameraSetup 
        mainVideoRef={mainVideoRef}
        pipVideoRef={pipVideoRef}
        onStreamsReady={handleStreamsReady}
        onReadyToRecord={handleReadyToRecord}
      />

      {/* Emotion detector */}
      <EmotionDetector
        videoRef={pipVideoRef}
        onEmotionDetected={(emotion: string, emoji: string, percentage: string | null) => {
          if (typeof handleEmotionDetected === 'function') {
            handleEmotionDetected(emotion, emoji, percentage);
          }
        }}
        onEmotionFrequencyUpdate={(frequencies) => {
          if (typeof handleEmotionFrequencyUpdate === 'function') {
            try {
              // Parse the JSON string to get the emotion frequencies object
              const parsedFrequencies = JSON.parse(frequencies);
              handleEmotionFrequencyUpdate(parsedFrequencies);
            } catch (error) {
              console.error('Error parsing emotion frequencies:', error);
            }
          }
        }}
      />

      {/* Recording timer with automatic stop after 15 seconds */}
      {isRecording && (
        <RecordingTimer 
          recordingTime={recordingTime}
          maxRecordingTime={15} // 15 seconds max recording time
          onMaxTimeReached={stopRecording} // Auto stop when max time is reached
        />
      )}

      {/* Recording controls */}
      {!recordedVideo ? (
        <RecordingControls
          isRecording={isRecording}
          isReadyToRecord={isReadyToRecord}
          onStartRecording={startRecording}
          onStopRecording={stopRecording}
          onCancel={cancelRecording}
        />
      ) : (
        <RecordedVideoReview
          recordedVideo={recordedVideo}
          dominantEmotion={dominantEmotion || "neutral"}
          dominantEmoji={dominantEmoji || ""}
          dominantEmotionPercentage={dominantEmotionPercentage}
          selectedEmotion={selectedEmotion}
          selectedEmoji={selectedEmoji}
          title={title}
          description={description}
          setTitle={setTitle}
          setDescription={setDescription}
          onSelectEmotion={selectAsDominantEmotion}
          onShareToSocial={shareToSocialMedia}
          onSave={saveRecordingAndSignIn}
          onCancel={cancelRecording}
          isSaving={isSaving}
          merchantId={merchantId}
        />
      )}
    </div>
  );
};

// Named export for backward compatibility
export const DualCameraDisplay = DualCameraView;

// Default export
export default DualCameraView;
